// src/pages/ResetPassword.tsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../api/auth.api.ts';
import { PageHeader } from '../components/PageHeader.tsx';
import { Footer } from '../components/Footer.tsx';
import './ResetPassword.css';

export const ResetPassword: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [username, setUsername] = useState<string>('');

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('Invalid reset link');
        setVerifying(false);
        return;
      }

      try {
        const result = await authAPI.verifyResetToken(token);
        setTokenValid(result.valid);
        if (result.username) {
          setUsername(result.username);
        }
        if (!result.valid) {
          setError('This reset link is invalid or has expired. Please request a new one.');
        }
      } catch (err) {
        setError('Failed to verify reset link');
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const validatePassword = (): boolean => {
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      setError('Password must contain uppercase, lowercase, and number');
      return false;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validatePassword()) {
      return;
    }

    if (!token) {
      setError('Invalid reset token');
      return;
    }

    try {
      setLoading(true);
      await authAPI.resetPassword(token, password);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Loading state while verifying token
  if (verifying) {
    return (
      <div className="reset-password-page">
        <div className="reset-main">
          <PageHeader
            icon="🔐"
            title="Reset Password"
            description="Verifying your reset link..."
          />
          <div className="reset-password-content">
            <div className="reset-form-container">
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div className="loading-spinner">⏳</div>
                <p>Verifying your reset link...</p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="reset-password-page">
        <div className="reset-main">
          <PageHeader
            icon="✅"
            title="Password Reset"
            description="Your password has been changed"
          />
          <div className="reset-password-content">
            <div className="reset-form-container">
              <div className="success-message">
                <div className="success-icon">✅</div>
                <h2>Password Reset Successful!</h2>
                <p>Your password has been changed successfully.</p>
                <p>You can now sign in with your new password.</p>
                <button
                  className="reset-submit-btn"
                  onClick={() => navigate('/')}
                >
                  Go to Sign In
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <div className="reset-password-page">
        <div className="reset-main">
          <PageHeader
            icon="⚠️"
            title="Reset Password"
            description="There was a problem with your reset link"
          />
          <div className="reset-password-content">
            <div className="reset-form-container">
              <div className="error-message">
                <div className="error-icon">⚠️</div>
                <h2>Invalid or Expired Link</h2>
                <p>{error || 'This password reset link is no longer valid.'}</p>
                <p>Please request a new password reset.</p>
                <button
                  className="reset-submit-btn"
                  onClick={() => navigate('/')}
                >
                  Back to Sign In
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Reset form
  return (
    <div className="reset-password-page">
      <div className="reset-main">
        <PageHeader
          icon="🔐"
          title="Reset Password"
          description={username ? `Create a new password for ${username}` : 'Create a new password'}
        />
        <div className="reset-password-content">
          <div className="reset-form-container">
            <form onSubmit={handleSubmit} className="reset-form">
              <h2>Create New Password</h2>
              <p className="reset-subtitle">
                Enter your new password below.
              </p>

              {error && <div className="reset-error">{error}</div>}

              <div className="form-group">
                <label htmlFor="new-password">New Password</label>
                <div className="password-input">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    disabled={loading}
                    autoComplete="new-password"
                    autoFocus
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                <span className="field-hint">
                  At least 8 characters with uppercase, lowercase, and number
                </span>
              </div>

              <div className="form-group">
                <label htmlFor="confirm-password">Confirm New Password</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="confirm-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>

              <button type="submit" className="reset-submit-btn" disabled={loading}>
                {loading ? 'Resetting Password...' : 'Reset Password'}
              </button>

              <div className="reset-footer">
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => navigate('/')}
                >
                  Back to Sign In
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ResetPassword;
